import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@/services/api/client';
import { reviewService } from '@/services/api/review.service';
import type { AddNoteRequest, DecisionRequest } from '@/types';
import { queryKeys } from './keys';

/**
 * Server state for one share link.
 *
 * The token is part of every key, so opening a second link in another tab does
 * not read the first one's cache.
 */

export function useOpenLink(token: string, passcode?: string) {
  return useQuery({
    queryKey: queryKeys.review.open(token),
    queryFn: ({ signal }) => reviewService.open(token, passcode, signal),
    enabled: token.length > 0,
    /*
     * Not retried on a refused link.
     *
     * A wrong passcode, a withdrawn link and an expired one all fail the same
     * way every time. Retrying three times makes the dentist wait to be told
     * something we already knew, and hammers an endpoint reachable without any
     * credential at all.
     */
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.needsPasscode || error.isDead)) return false;
      return failureCount < 2;
    },
    /*
     * The design does not change under the dentist. A bundle is immutable -
     * publishing again creates a new version with its own link - so refetching
     * on every window focus would re-download a mesh to get the same bytes.
     */
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useAddNote(token: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: AddNoteRequest) => reviewService.addNote(token, body),
    onSuccess: async () => {
      // Refetch rather than push the new note into the cache by hand: the
      // server assigns the id and the timestamp, and a note rendered from a
      // guess at those is a note that jumps when the truth arrives.
      await queryClient.invalidateQueries({ queryKey: queryKeys.review.open(token) });
    },
  });
}

export function useDecide(token: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: DecisionRequest) => reviewService.decide(token, body),
    onSuccess: async () => {
      // The decision is part of what /open returns, and it is what switches the
      // panel from asking to reporting.
      await queryClient.invalidateQueries({ queryKey: queryKeys.review.open(token) });
    },
  });
}
